import {
    AcquisitionJourneyEventDetailsJourneyTypes,
    AcquisitionJourneyCardsTableEventBase,
} from '@appTypes/AcquisitionEvents.interface'
import {
    CreditCardAlternativeProduct,
    ScoredCreditCardProduct,
} from '@appTypes/EligibilityApi.interface'

export interface AcquisitionBasinPayload
    extends AcquisitionJourneyCardsTableEventBase {
    personal_data: any
    user_id: string
}

export interface AcquisitionDataLayerPayload
    extends AcquisitionJourneyCardsTableEventBase {}

export interface AcquisitionJourneyTrackingData {
    basinPayload: AcquisitionBasinPayload
    dataLayerPayload: AcquisitionDataLayerPayload
}

function getAcquisitionJourneyTrackingData() {
    try {
        const rawData =
            window.sessionStorage.getItem('registrationTracking') ||
            window.sessionStorage.getItem('trackingData')

        if (!rawData) {
            return null
        }

        const parsedJson = JSON.parse(rawData)

        if (
            !parsedJson ||
            !('basinPayload' in parsedJson) ||
            !('dataLayerPayload' in parsedJson)
        ) {
            throw Error('Invalid tracking data')
        }

        return parsedJson as AcquisitionJourneyTrackingData
    } catch (error) {
        console.error(error)
        return null
    }
}

function buildScoredTableOutcomeObject(
    isNewSignup: boolean,
    sortedProducts?: ScoredCreditCardProduct[]
) {
    if (!sortedProducts) {
        return {
            best_offer: undefined,
            outcome: 'accept',
            outcome_number: 1,
            products: undefined,
            results: undefined,
            nlg: isNewSignup,
        }
    }

    return {
        best_offer: sortedProducts[0].productCode,
        outcome: 'accept',
        outcome_number: 1,
        products: sortedProducts
            .map(item => item.productCode)
            .filter(code => code)
            .join('|'),
        results: sortedProducts.length,
        nlg: isNewSignup,
    }
}

function buildAlternativeProductsTableOutcomeObject(
    sortedProducts: CreditCardAlternativeProduct[],
    isNewSignUp: boolean
) {
    return {
        best_offer: sortedProducts[0].productCode,
        outcome: 'decline',
        outcome_number: 1,
        products: sortedProducts
            .map(item => item.productCode)
            .filter(code => code)
            .join('|'),
        results: sortedProducts.length,
        nlg: isNewSignUp,
    }
}

function buildAlternativeProductsTableClickoutObject(
    product: CreditCardAlternativeProduct,
    sortedProducts: CreditCardAlternativeProduct[]
) {
    return {
        clickout_apr: product.apr,
        description: product.header,
        lender: undefined,
        likelihood: undefined,
        selected_product: product.productCode,
        results_number: sortedProducts.indexOf(product) + 1,
    }
}

const eventDetails = (
    step: 'productTable' | 'alternativeProductTable',
    experimentsFromSessionStorage?: string,
    eventDetails?: AcquisitionJourneyEventDetailsJourneyTypes
) => ({
    journey: eventDetails ?? 'Credit Cards Signup',
    step,
    step_url: window.location.origin + window.location.pathname,
    experiment_ids: experimentsFromSessionStorage,
})

export function buildAcquisitionJourneyFormAcceptEventPayload(
    products: ScoredCreditCardProduct[],
    isNewSignUpJourney: boolean
) {
    const acquisitionJourneyTrackingData = getAcquisitionJourneyTrackingData()

    if (!!acquisitionJourneyTrackingData) {
        const basePayload = {
            event_name: 'form_accept' as const,
            event_details: eventDetails(
                'productTable',
                acquisitionJourneyTrackingData.basinPayload.event_details
                    ?.experiment_ids,
                acquisitionJourneyTrackingData.basinPayload.event_details
                    ?.journey
            ),
            event_ts: new Date().toISOString(),
            outcome: buildScoredTableOutcomeObject(
                isNewSignUpJourney,
                products
            ),
        }

        return {
            basinPayload: {
                ...acquisitionJourneyTrackingData.basinPayload,
                ...basePayload,
            },
            dataLayerPayload: {
                ...acquisitionJourneyTrackingData.dataLayerPayload,
                ...basePayload,
            },
        }
    }
}

export function buildAcquisitionJourneyFormDeclineEventPayload(
    products: CreditCardAlternativeProduct[],
    isNewSignUpJourney: boolean
) {
    const acquisitionJourneyTrackingData = getAcquisitionJourneyTrackingData()

    if (!!acquisitionJourneyTrackingData) {
        const basePayload = {
            event_name: 'form_decline' as const,
            event_details: eventDetails(
                'alternativeProductTable',
                acquisitionJourneyTrackingData.basinPayload.event_details
                    ?.experiment_ids
            ),
            event_ts: new Date().toISOString(),
            outcome: buildAlternativeProductsTableOutcomeObject(
                products,
                isNewSignUpJourney
            ),
        }

        return {
            basinPayload: {
                ...acquisitionJourneyTrackingData.basinPayload,
                ...basePayload,
            },
            dataLayerPayload: {
                ...acquisitionJourneyTrackingData.dataLayerPayload,
                ...basePayload,
            },
        }
    }
}

export function buildAcquisitionJourneyScoredTableClickoutEventPayload(
    product: ScoredCreditCardProduct,
    isNewSignUpJourney: boolean,
    sortedProducts?: ScoredCreditCardProduct[]
) {
    const acquisitionJourneyTrackingData = getAcquisitionJourneyTrackingData()

    if (!!acquisitionJourneyTrackingData) {
        const basePayload = {
            clickout: {
                clickout_apr: product.offerDetail.purchaseAER,
                description: product.name,
                lender: product.provider,
                likelihood: product.score,
                selected_product: product.productCode,
                results_number: sortedProducts
                    ? sortedProducts.indexOf(product) + 1
                    : undefined,
            },
            event_name: 'form_clickout' as const,
            event_details: eventDetails(
                'productTable',
                acquisitionJourneyTrackingData.basinPayload?.event_details
                    ?.experiment_ids
            ),
            event_ts: new Date().toISOString(),
            outcome: buildScoredTableOutcomeObject(
                isNewSignUpJourney,
                sortedProducts
            ),
        }

        return {
            basinPayload: {
                ...acquisitionJourneyTrackingData.basinPayload,
                ...basePayload,
            },
            dataLayerPayload: {
                ...acquisitionJourneyTrackingData.dataLayerPayload,
                ...basePayload,
            },
        }
    }
}

export function buildAcquisitionJourneyAlternativeProductsTableClickoutEventPayload(
    product: CreditCardAlternativeProduct,
    sortedProducts: CreditCardAlternativeProduct[],
    isNewSignUpJourney: boolean
) {
    const acquisitionJourneyTrackingData = getAcquisitionJourneyTrackingData()

    if (!!acquisitionJourneyTrackingData) {
        const basePayload = {
            clickout: buildAlternativeProductsTableClickoutObject(
                product,
                sortedProducts
            ),
            event_name: 'form_clickout' as const,
            event_details: eventDetails(
                'alternativeProductTable',
                acquisitionJourneyTrackingData.basinPayload.event_details
                    ?.experiment_ids
            ),
            event_ts: new Date().toISOString(),
            outcome: buildAlternativeProductsTableOutcomeObject(
                sortedProducts,
                isNewSignUpJourney
            ),
        }

        return {
            basinPayload: {
                ...acquisitionJourneyTrackingData.basinPayload,
                ...basePayload,
            },
            dataLayerPayload: {
                ...acquisitionJourneyTrackingData.dataLayerPayload,
                ...basePayload,
            },
        }
    }
}
